import type {
  WorkflowActionType,
  WorkflowAssignmentType,
  WorkflowDetail,
  WorkflowStageView,
} from "@closer/shared";
import { WORKFLOW_ACTION_TYPE_NAMES, WORKFLOW_ASSIGNMENT_TYPE_NAMES } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FlowConnector,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * İŞ AKIŞI TASARIMCISI (Dinamik Workflow — Kart 7).
 *
 * ### ⚠ Serbest canvas DEĞİL, dikey aşama zinciri
 *
 * Akış lineer (UI/UX §12 MUST) — sıra tek boyutluysa bir node'un (x, y) konumu hiçbir şey
 * ifade etmez. Serbest düzlem, §12'nin yasakladığı dallanmayı görsel olarak MÜMKÜN kılar ve
 * onu engellemek için ayrıca kısıt yazdırırdı. Teknoloji karşılaştırması:
 * `docs/DINAMIK_WORKFLOW_KART7_TEKNOLOJI_ONERISI.md`.
 *
 * ### ⚠ Sıralamanın KANONİK yolu klavye
 *
 * Taşıma düğmeleri asıl yol; sürükleme (geldiğinde) onun üstüne bir geliştirme. UI/UX §71
 * canvas etkileşiminin erişilebilir bir alternatifle desteklenmesini istiyor — bu, o
 * maddenin kendisi, ondan bir sapma değil.
 *
 * ### ⚠ Yayınlanmış sürüm YERİNDE düzenlenmiyor
 *
 * Taslağı olmayan akışta düzenleme yüzeyi hiç açılmıyor; "Yeni sürüm oluştur" ayrı ve açık
 * bir eylem (Kart 2, UI/UX §39). Aksi hâlde kullanıcı yürüyen akışları geçmişe dönük
 * değiştirdiğini fark etmezdi.
 *
 * ### ⚠ Bu ekran YETKİ VERMİYOR
 *
 * Bir kullanıcıyı aşamaya koymak ona hiçbir permission kazandırmaz (kanonik spesifikasyon
 * §24). Yetenek ve kayıt erişimi aktivasyon anında yeniden çözülüyor (Kart 4).
 */

type Taslak = WorkflowStageView & { yerelKimlik: string };

const AKSIYON_SECENEKLERI: WorkflowActionType[] = ["full", "review", "revision", "approval"];
const ATAMA_SECENEKLERI: WorkflowAssignmentType[] = ["user_based", "role_based"];

function bosAsama(sira: number): Taslak {
  return {
    yerelKimlik: crypto.randomUUID(),
    id: crypto.randomUUID(),
    orderIndex: sira,
    name: "Yeni aşama",
    instruction: null,
    assignmentType: "user_based",
    actionType: "full",
    completionPolicy: null,
    partialMode: null,
    requiredCount: null,
    targetUserIds: [],
    targetRoleIds: [],
    requiredUserIds: [],
  };
}

export function WorkflowBuilderPage() {
  const { id = "" } = useParams();
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();

  const yonetebilir = can("workflow.manage");
  const yayinlayabilir = can("workflow.publish");

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "workflow", id),
    queryFn: async () => (await api<WorkflowDetail>(`/api/workflows/${id}`, { tenantId })).data,
  });

  const rehber = useQuery({
    enabled: yonetebilir,
    queryKey: tenantKey(tenantId, "workflow-directory"),
    queryFn: async () =>
      (
        await api<{
          users: { userId: string; name: string }[];
          roles: { id: string; name: string }[];
        }>("/api/workflows/directory", { tenantId })
      ).data,
  });

  const [asamalar, setAsamalar] = useState<Taslak[]>([]);
  const [secili, setSecili] = useState<string | null>(null);
  const [yuklenenSurum, setYuklenenSurum] = useState<string | null>(null);

  /**
   * ⚠ SUNUCU VERİSİ YEREL DURUMA YALNIZ SÜRÜM DEĞİŞTİĞİNDE kopyalanıyor.
   *
   * Her yanıtta kopyalasaydık, kaydetme sonrası dönen yanıt kullanıcının o sırada yaptığı
   * düzenlemeyi sessizce geri alırdı. `useEffect` yerine render sırasında eşitleniyor:
   * effect ekstra bir tur render yaptırır ve arada kullanıcı eski listeyi görürdü.
   */
  const surumAnahtari = sorgu.data ? `${sorgu.data.id}:${sorgu.data.draftVersionId ?? "-"}` : null;
  if (sorgu.data && surumAnahtari !== yuklenenSurum) {
    setYuklenenSurum(surumAnahtari);
    setAsamalar(sorgu.data.stages.map((a) => ({ ...a, yerelKimlik: crypto.randomUUID() })));
    setSecili(null);
  }

  const kaydet = useMutation({
    mutationFn: async () =>
      (
        await api<WorkflowDetail>(`/api/workflows/${id}/draft/stages`, {
          method: "PUT",
          tenantId,
          body: JSON.stringify({
            stages: asamalar.map((a) => ({
              name: a.name,
              instruction: a.instruction,
              assignmentType: a.assignmentType,
              actionType: a.actionType,
              completionPolicy: a.completionPolicy,
              partialMode: a.partialMode,
              requiredCount: a.requiredCount,
              targetUserIds: a.targetUserIds,
              targetRoleIds: a.targetRoleIds,
              requiredUserIds: a.requiredUserIds,
            })),
          }),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workflow", id) });
      toast.success("Aşamalar kaydedildi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Aşamalar kaydedilemedi"),
  });

  const yayinla = useMutation({
    mutationFn: async () =>
      (await api<WorkflowDetail>(`/api/workflows/${id}/publish`, { method: "POST", tenantId }))
        .data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workflow", id) });
      toast.success("İş akışı yayınlandı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İş akışı yayınlanamadı"),
  });

  const taslakAc = useMutation({
    mutationFn: async () =>
      (await api<WorkflowDetail>(`/api/workflows/${id}/draft`, { method: "POST", tenantId })).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "workflow", id) });
      toast.success("Yeni taslak sürüm açıldı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Yeni sürüm açılamadı"),
  });

  if (sorgu.isPending) return <Skeleton lines={6} />;
  if (sorgu.isError) {
    return <StateView state={resolveErrorState(sorgu.error, { idempotent: true })} />;
  }

  const akis = sorgu.data!;
  const taslakVar = akis.draftVersionId !== null;
  const duzenlenebilir = yonetebilir && taslakVar;

  function asamaEkle(sira: number) {
    setAsamalar((onceki) => {
      const kopya = [...onceki];
      kopya.splice(sira, 0, bosAsama(sira));
      return kopya.map((a, i) => ({ ...a, orderIndex: i }));
    });
  }

  /** ⚠ Sıra GÖNDERİM SIRASINDAN türüyor; `orderIndex` yalnız gösterim için tazeleniyor. */
  function tasi(sira: number, yon: -1 | 1) {
    setAsamalar((onceki) => {
      const hedef = sira + yon;
      if (hedef < 0 || hedef >= onceki.length) return onceki;
      const kopya = [...onceki];
      const [tasinan] = kopya.splice(sira, 1);
      kopya.splice(hedef, 0, tasinan!);
      return kopya.map((a, i) => ({ ...a, orderIndex: i }));
    });
  }

  function sil(yerelKimlik: string) {
    setAsamalar((onceki) =>
      onceki.filter((a) => a.yerelKimlik !== yerelKimlik).map((a, i) => ({ ...a, orderIndex: i })),
    );
    setSecili((s) => (s === yerelKimlik ? null : s));
  }

  function guncelle(yerelKimlik: string, degisiklik: Partial<Taslak>) {
    setAsamalar((onceki) =>
      onceki.map((a) => (a.yerelKimlik === yerelKimlik ? { ...a, ...degisiklik } : a)),
    );
  }

  const seciliAsama = asamalar.find((a) => a.yerelKimlik === secili) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={akis.name}
        description={
          taslakVar
            ? `Taslak sürüm v${akis.draftVersionNumber} düzenleniyor`
            : "Yayınlanmış sürüm görüntüleniyor; düzenlemek için yeni sürüm oluşturun"
        }
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={akis.status === "published" ? "success" : "neutral"}>
              {akis.publishedVersionNumber !== null
                ? `Yayında: v${akis.publishedVersionNumber}`
                : "Yayınlanmış sürüm yok"}
            </Badge>
            {duzenlenebilir ? (
              <Button size="sm" onClick={() => kaydet.mutate()} loading={kaydet.isPending}>
                Kaydet
              </Button>
            ) : null}
            {duzenlenebilir && yayinlayabilir ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => yayinla.mutate()}
                loading={yayinla.isPending}
              >
                Yayınla
              </Button>
            ) : null}
            {yonetebilir && !taslakVar ? (
              <Button size="sm" onClick={() => taslakAc.mutate()} loading={taslakAc.isPending}>
                Yeni sürüm oluştur
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col items-stretch">
          {asamalar.length === 0 ? (
            <EmptyState
              title="Henüz aşama yok"
              description={
                duzenlenebilir
                  ? "Sözleşmenin şirket içinde geçeceği ilk adımı ekleyerek başlayın."
                  : "Bu sürümde tanımlı aşama bulunmuyor."
              }
              action={
                duzenlenebilir ? (
                  <Button size="sm" onClick={() => asamaEkle(0)}>
                    Aşama ekle
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ol className="flex flex-col">
              {asamalar.map((a, sira) => (
                <li key={a.yerelKimlik} className="flex flex-col">
                  {/** ⚠ Araya ekleme KONEKTÖRÜN üzerinde (§32): niyet iki adıma bölünmüyor. */}
                  {sira === 0 ? null : (
                    <FlowConnector
                      onInsert={duzenlenebilir ? () => asamaEkle(sira) : undefined}
                      insertLabel="Araya aşama ekle"
                    />
                  )}
                  {/** ⚠ Kart TIKLANABİLİR DEĞİL: seçim ayrı ve adlandırılmış bir düğmeyle
                   * yapılıyor. Tıklanabilir bir kutu klavyeye ve ekran okuyucuya görünmez;
                   * §71 temel aksiyonların klavyeyle erişilebilir olmasını istiyor. */}
                  <Card className={a.yerelKimlik === secili ? "border-primary" : undefined}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-fg-strong">{a.name}</span>
                        <span className="text-xs text-fg-soft">
                          {sira + 1}. adım · {WORKFLOW_ACTION_TYPE_NAMES[a.actionType]} ·{" "}
                          {WORKFLOW_ASSIGNMENT_TYPE_NAMES[a.assignmentType]}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`${a.name} ayarlarını aç`}
                          onClick={() => setSecili(a.yerelKimlik)}
                        >
                          Ayarlar
                        </Button>
                        {duzenlenebilir ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`${a.name} aşamasını yukarı taşı`}
                              disabled={sira === 0}
                              onClick={() => tasi(sira, -1)}
                            >
                              ↑
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`${a.name} aşamasını aşağı taşı`}
                              disabled={sira === asamalar.length - 1}
                              onClick={() => tasi(sira, 1)}
                            >
                              ↓
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`${a.name} aşamasını sil`}
                              onClick={() => sil(a.yerelKimlik)}
                            >
                              Sil
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </Card>
                </li>
              ))}
            </ol>
          )}

          {duzenlenebilir && asamalar.length > 0 ? (
            <div className="pt-2">
              <Button size="sm" variant="secondary" onClick={() => asamaEkle(asamalar.length)}>
                Sona aşama ekle
              </Button>
            </div>
          ) : null}
        </div>

        {/** ⚠ Inspector: seçili aşamanın ÜÇ EKSENİ. Kurallar sunucuda da var; burada yalnız
         * yansıtılıyorlar — şema hatası forma düşüyor. */}
        <Card>
          {seciliAsama === null ? (
            <p className="text-sm text-fg-soft">Ayarlarını görmek için bir aşama seçin.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <FormField label="Aşama adı">
                <Input
                  id="asama-adi"
                  value={seciliAsama.name}
                  disabled={!duzenlenebilir}
                  onChange={(olay) =>
                    guncelle(seciliAsama.yerelKimlik, { name: olay.target.value })
                  }
                />
              </FormField>
              <FormField label="Açıklama (opsiyonel)">
                <Textarea
                  id="asama-aciklama"
                  rows={2}
                  value={seciliAsama.instruction ?? ""}
                  disabled={!duzenlenebilir}
                  onChange={(olay) =>
                    guncelle(seciliAsama.yerelKimlik, { instruction: olay.target.value || null })
                  }
                />
              </FormField>
              <FormField label="İşlem tipi">
                <Select
                  id="asama-islem"
                  value={seciliAsama.actionType}
                  disabled={!duzenlenebilir}
                  onChange={(olay) =>
                    guncelle(seciliAsama.yerelKimlik, {
                      actionType: olay.target.value as WorkflowActionType,
                    })
                  }
                >
                  {AKSIYON_SECENEKLERI.map((t) => (
                    <option key={t} value={t}>
                      {WORKFLOW_ACTION_TYPE_NAMES[t]}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Atama tipi">
                <Select
                  id="asama-atama"
                  value={seciliAsama.assignmentType}
                  disabled={!duzenlenebilir}
                  onChange={(olay) =>
                    guncelle(seciliAsama.yerelKimlik, {
                      assignmentType: olay.target.value as WorkflowAssignmentType,
                      /** ⚠ Tip değişince ESKİ hedefler temizleniyor: kullanıcı bazlı aşamaya
                       * rol, rol bazlı aşamaya kullanıcı atanamıyor (sunucu da reddeder). */
                      targetUserIds: [],
                      targetRoleIds: [],
                      requiredUserIds: [],
                      completionPolicy: olay.target.value === "role_based" ? "all" : null,
                      partialMode: null,
                      requiredCount: null,
                    })
                  }
                >
                  {ATAMA_SECENEKLERI.map((t) => (
                    <option key={t} value={t}>
                      {WORKFLOW_ASSIGNMENT_TYPE_NAMES[t]}
                    </option>
                  ))}
                </Select>
              </FormField>

              {seciliAsama.assignmentType === "user_based" ? (
                <FormField label="Kullanıcılar">
                  <div className="flex flex-col gap-1">
                    {(rehber.data?.users ?? []).map((k) => (
                      <Checkbox
                        key={k.userId}
                        label={k.name}
                        disabled={!duzenlenebilir}
                        checked={seciliAsama.targetUserIds.includes(k.userId)}
                        onChange={(olay) =>
                          guncelle(seciliAsama.yerelKimlik, {
                            targetUserIds: olay.target.checked
                              ? [...seciliAsama.targetUserIds, k.userId]
                              : seciliAsama.targetUserIds.filter((x) => x !== k.userId),
                          })
                        }
                      />
                    ))}
                  </div>
                </FormField>
              ) : (
                <FormField label="Roller">
                  <div className="flex flex-col gap-1">
                    {(rehber.data?.roles ?? []).map((r) => (
                      <Checkbox
                        key={r.id}
                        label={r.name}
                        disabled={!duzenlenebilir}
                        checked={seciliAsama.targetRoleIds.includes(r.id)}
                        onChange={(olay) =>
                          guncelle(seciliAsama.yerelKimlik, {
                            targetRoleIds: olay.target.checked
                              ? [...seciliAsama.targetRoleIds, r.id]
                              : seciliAsama.targetRoleIds.filter((x) => x !== r.id),
                          })
                        }
                      />
                    ))}
                  </div>
                </FormField>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
